// client/src/components/pages/LexiStar/Approvals/approvalLogic.js
//
// Pure helpers for the approvals queue: queue order (severity, then age —
// LS-09 §3 attention queue), the SLA countdown, and the keyboard map
// (LS-09 §5.4: A approve, R reject, E escalate, J/K next/previous).

import { severityMeta } from '../StatusBoard/statusLanguage';

/**
 * Decision SLA when the row carries no `sla_due_at` (m2-contracts §4.2 does not
 * name one yet): minutes from opening, by severity. A UI default, to be
 * replaced by the tenant's notification-rule escalation times.
 */
export const DEFAULT_SLA_MIN = { C: 5, H: 15, M: 30, L: 60, I: 120 };

export const QUEUE_FILTERS = [
  { value: 'pending', label: 'Needs decision', states: 'OPEN,AWAITING_DECISION' },
  { value: 'escalated', label: 'Escalated', states: 'ESCALATED' },
  { value: 'decided', label: 'Decided', states: 'APPROVED,REJECTED' },
];

/** Most severe first, then oldest first. */
export function sortQueue(rows) {
  return [...rows].sort((a, b) => {
    const s = severityMeta(b.severity).rank - severityMeta(a.severity).rank;
    if (s !== 0) return s;
    return new Date(a.opened_at).getTime() - new Date(b.opened_at).getTime();
  });
}

export function slaDueAt(row) {
  if (row.sla_due_at) return new Date(row.sla_due_at).getTime();
  const opened = new Date(row.opened_at).getTime();
  if (Number.isNaN(opened)) return null;
  return opened + (DEFAULT_SLA_MIN[row.severity] ?? DEFAULT_SLA_MIN.M) * 60000;
}

/** { text: '8:42' | 'overdue 2:05', overdue } */
export function slaCountdown(row, now = Date.now()) {
  const due = slaDueAt(row);
  if (due === null) return { text: '—', overdue: false };
  const diff = Math.round((due - now) / 1000);
  const abs = Math.abs(diff);
  const text = `${Math.floor(abs / 60)}:${String(abs % 60).padStart(2, '0')}`;
  return diff >= 0 ? { text, overdue: false } : { text: `overdue ${text}`, overdue: true };
}

export const KEY_ACTIONS = {
  a: 'approve',
  r: 'reject',
  e: 'escalate',
  j: 'next',
  k: 'previous',
};

/** Ignore shortcuts while typing, in a picker, or with a modifier held. */
export function shortcutFor(event) {
  if (event.defaultPrevented || event.ctrlKey || event.metaKey || event.altKey) return null;
  const t = event.target;
  const tag = t?.tagName;
  if (tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT' || t?.isContentEditable)
    return null;
  if (t?.closest?.('[role="dialog"]')) return null;
  return KEY_ACTIONS[String(event.key).toLowerCase()] || null;
}

export const ACTIONS = {
  approve: {
    label: 'Approve once',
    key: 'A',
    verb: 'Approved',
    className: 'bg-primary-500 hover:bg-primary-600 text-white',
  },
  approve_corridor: {
    label: 'Approve + temporary corridor',
    key: null,
    verb: 'Approved with a temporary corridor',
    className: 'bg-surface border border-primary-400 text-primary-700 hover:bg-primary-50',
  },
  reject: {
    label: 'Reject — return to route',
    key: 'R',
    verb: 'Rejected',
    className: 'bg-surface border border-slate-300 text-slate-800 hover:bg-slate-50',
  },
  escalate: {
    label: 'Escalate',
    key: 'E',
    verb: 'Escalated',
    className: 'bg-surface border border-slate-300 text-slate-800 hover:bg-slate-50',
  },
};
